import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProposalBuildStatus, ProposalSummary } from '@shared/api';
import { listProposals } from '../../api/client';
import { navigate } from '../../state/router';
import type { AuthStore } from '../../state/useAuth';
import { useTheme } from '../../state/useTheme';
import { ChevronRightIcon, MagnifierIcon } from '../icons';
import { LoginModal } from '../LoginModal';
import { AccountMenu } from './AccountMenu';
import { CreditsMeter } from './CreditsMeter';
import { ProposalRow } from './ProposalRow';

/** The Proposal wordmark in the sidebar's top-left (and just its "P" in the
 * collapsed rail). One image per theme; CSS shows the one that matches.
 * The agency's own brand lives in the account menu at the foot of the rail. */
function ProposalLogo({ variant }: { variant: 'wordmark' | 'mark' }): ReactElement {
  return (
    <span className={`proposal-logo proposal-logo-${variant}`}>
      <img className="proposal-logo-dark" src={`/brand/proposal-${variant}-dark.webp`} alt="Proposal" />
      <img className="proposal-logo-light" src={`/brand/proposal-${variant}-light.webp`} alt="" aria-hidden="true" />
    </span>
  );
}

function PlusIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2.5v11M2.5 8h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** The 16px variant that sits in the 24px accent tile of the header row. */
function PlusGlyph(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CloseGlyph(): ReactElement {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The persistent left rail (design.md V9, restructured in V11 item 8): the
 * Proposals mark, the 36px control row, a "Recent" eyebrow over the
 * data-driven proposal list, then the signed-in rep's account button. Its
 * menu (AccountMenu) carries the org switch, settings, dark mode and log
 * out — per user 2026-10-01, since most reps have one org the switch lives
 * there rather than in the top-left. Rendered by BOTH StartView and
 * ProposalView via AppShell.
 *
 * The control row (spec option 3a) carries "New proposal" and the search
 * button side by side at rest, with the search field as an absolutely
 * positioned OVERLAY on top of both rather than a width-trade: the row
 * underneath never resizes, so nothing reflows mid-gesture and there is no
 * mis-click window while widths interpolate.
 */
export function Sidebar({
  auth,
  activeProposalId,
  advertiserName,
  buildStatus,
  collapsed,
  onToggleCollapsed,
}: {
  auth: AuthStore;
  activeProposalId: string | null;
  advertiserName: string | null;
  buildStatus?: ProposalBuildStatus;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}): ReactElement {
  const [recent, setRecent] = useState<ProposalSummary[]>([]);
  const [loginOpen, setLoginOpen] = useState(false);
  const themeStore = useTheme();
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchToggleRef = useRef<HTMLButtonElement>(null);

  // Search overlays the control row, so it only exists in the expanded rail:
  // opening it from the collapsed rail expands first.
  const openSearch = useCallback(() => {
    if (collapsed) onToggleCollapsed();
    setSearchOpen(true);
  }, [collapsed, onToggleCollapsed]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearch('');
    searchToggleRef.current?.focus();
  }, []);

  // One place to take focus, so the field is focused whichever way it opened
  // (button, rail button, hotkey) and only once it is actually on screen -
  // it is pointer-events:none until [data-open] lands.
  useEffect(() => {
    if (searchOpen && !collapsed) searchInputRef.current?.focus();
  }, [searchOpen, collapsed]);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openSearch]);

  // Rows that weren't in the previous fetch slide in at the top (a new
  // proposal). The very first fetch only records what is there.
  const seenIdsRef = useRef<Set<string> | null>(null);
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let live = true;
    void listProposals()
      .then((res) => {
        if (!live) return;
        const seen = seenIdsRef.current;
        setEnteringIds(new Set(seen ? res.proposals.map((p) => p.proposalId).filter((id) => !seen.has(id)) : []));
        seenIdsRef.current = new Set(res.proposals.map((p) => p.proposalId));
        setRecent(res.proposals);
      })
      .catch(() => {
        // A quiet list is allowed to be absent.
      });
    return () => {
      live = false;
    };
    // Cheap re-fetch whenever the active proposal or its advertiser name
    // changes, so a rename shows up in the list without building live sync.
  }, [activeProposalId, advertiserName, buildStatus, refreshTick]);

  // While the agent is working on any proposal, poll so its row's status
  // line follows the build and the cover thumbnail lands when it finishes.
  const anyWorking = recent.some((item) => item.workingLabel);
  useEffect(() => {
    if (!anyWorking) return;
    const timer = window.setInterval(() => setRefreshTick((tick) => tick + 1), 2500);
    return () => window.clearInterval(timer);
  }, [anyWorking]);

  const status = auth.status;
  const signedIn = status?.signedIn ?? false;
  const filteredRecent = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return recent;
    return recent.filter((item) => `${item.proposalName ?? ''} ${item.advertiserName ?? ''}`.toLocaleLowerCase().includes(query));
  }, [recent, search]);

  // docs/viewer-sharing.md: a teammate's view-only proposals list under
  // their own heading, with whose they are, never mixed into the rep's own.
  const ownRecent = filteredRecent.filter((item) => item.access !== 'viewer');
  const sharedRecent = filteredRecent.filter((item) => item.access === 'viewer');

  const renderList = (items: ProposalSummary[]): ReactElement => (
    <ul className="sidebar-recent-list">
      {items.map((item) => (
        <ProposalRow
          key={item.proposalId}
          item={item}
          active={item.proposalId === activeProposalId}
          entering={enteringIds.has(item.proposalId)}
        />
      ))}
    </ul>
  );

  if (collapsed) {
    return <aside className="sidebar sidebar-rail">
      <button className="sidebar-rail-logo" type="button" onClick={onToggleCollapsed} aria-label="Expand sidebar" title="Expand sidebar (⌘\\)"><ProposalLogo variant="mark" /></button>
      <button type="button" className="sidebar-new sidebar-new-rail" onClick={() => navigate('/')} aria-label="New proposal" title="New proposal (⌘N)"><PlusIcon /></button>
      <span className="sidebar-rail-divider" />
      <button type="button" className="sidebar-rail-item sidebar-rail-item-active" onClick={onToggleCollapsed} aria-label="Pages" title="Pages"><span aria-hidden="true">▦</span></button>
      <button type="button" className="sidebar-rail-item" onClick={openSearch} aria-label="Search proposals" title="Search proposals (⌘K)"><MagnifierIcon /></button>
      <span className="sidebar-rail-spacer" />
      {signedIn ? (
        <AccountMenu auth={auth} themeStore={themeStore} variant="rail" />
      ) : (
        <button type="button" className="sidebar-rail-avatar" onClick={() => setLoginOpen(true)} aria-label="Sign in" title="Sign in">·</button>
      )}
      {loginOpen ? <LoginModal auth={auth} onClose={() => setLoginOpen(false)} /> : null}
    </aside>;
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <ProposalLogo variant="wordmark" />
      </div>

      <div className="sidebar-controls" data-search-open={searchOpen ? '' : undefined}>
        <div className="sidebar-controls-row">
          <button type="button" className="sidebar-new-btn" onClick={() => navigate('/')}>
            <span className="sidebar-new-tile" aria-hidden="true"><PlusGlyph /></span>
            <span className="sidebar-new-label">New proposal</span>
          </button>
          <button
            ref={searchToggleRef}
            type="button"
            className="sidebar-search-toggle"
            onClick={() => (searchOpen ? closeSearch() : openSearch())}
            aria-label="Search proposals"
            aria-expanded={searchOpen}
            aria-controls="sidebar-search-field"
            title="Search proposals (⌘K)"
          >
            <MagnifierIcon />
          </button>
        </div>

        <div className="sidebar-search-field" id="sidebar-search-field" data-open={searchOpen ? '' : undefined}>
          <MagnifierIcon />
          <input
            ref={searchInputRef}
            value={search}
            tabIndex={searchOpen ? 0 : -1}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); closeSearch(); } }}
            placeholder="Search proposals"
            aria-label="Search recent proposals"
          />
          <button type="button" className="sidebar-search-clear" tabIndex={searchOpen ? 0 : -1} onClick={closeSearch} aria-label="Close search">
            <CloseGlyph />
          </button>
        </div>
      </div>

      <div className="sidebar-recent">
        {ownRecent.length > 0 || sharedRecent.length === 0 ? <span className="sidebar-recent-label">Recent</span> : null}
        {ownRecent.length > 0 ? renderList(ownRecent) : null}
        {sharedRecent.length > 0 ? (
          <>
            <span className="sidebar-recent-label sidebar-recent-label-shared">Shared with me</span>
            {renderList(sharedRecent)}
          </>
        ) : null}
      </div>

      <div className="sidebar-footer">
        {signedIn ? (
          <CreditsMeter refreshKey={`${activeProposalId ?? ''}|${buildStatus ?? ''}|${status?.activeOrganizationId ?? ''}|${status?.branding.cobrand ?? ''}`} />
        ) : null}
        {signedIn && status?.username ? (
          <AccountMenu auth={auth} themeStore={themeStore} variant="full" />
        ) : (
          <button type="button" className="sidebar-account" onClick={() => setLoginOpen(true)}>
            <span className="sidebar-account-avatar" aria-hidden="true">
              ·
            </span>
            <span className="sidebar-account-body">
              <span className="sidebar-account-name">Sign in to VisitIQ</span>
            </span>
            <span className="sidebar-account-chevron" aria-hidden="true">
              <ChevronRightIcon />
            </span>
          </button>
        )}
      </div>
      {loginOpen ? <LoginModal auth={auth} onClose={() => setLoginOpen(false)} /> : null}
    </aside>
  );
}
